import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { generateCycles } from '@/lib/api/features/cycles/api'
import { useProjectCyclesEnabled } from '@/hooks/useProjectCyclesEnabled'
import useLoginStore from '@/store/LoginStore'

interface GenerateCyclesDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Prefill for "Schedule next N" (design 8a). */
  defaultCount?: number
  /** yyyy-MM-dd; e.g. the day after the last cycle ends. */
  defaultStartDate?: string
}

export function GenerateCyclesDialog({
  projectId,
  open,
  onOpenChange,
  defaultCount = 4,
  defaultStartDate = '',
}: GenerateCyclesDialogProps) {
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [count, setCount] = useState(String(defaultCount))
  // Re-seed the prefill each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setStartDate(defaultStartDate)
    setCount(String(defaultCount))
  }, [open, defaultStartDate, defaultCount])
  const [namePrefix, setNamePrefix] = useState('Sprint')
  const [isGenerating, setIsGenerating] = useState(false)
  const { cycleDurationWeeks } = useProjectCyclesEnabled(projectId)
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const handleGenerate = async () => {
    if (!startDate || !workspaceId || !cycleDurationWeeks) return
    setIsGenerating(true)
    try {
      await generateCycles(workspaceId, projectId, {
        startDate: new Date(startDate).toISOString(),
        durationWeeks: cycleDurationWeeks,
        count: Number(count),
        namePrefix: namePrefix.trim() || 'Sprint',
      })
      toast.success(`Generated ${count} cycles`)
      onOpenChange(false)
    } catch {
      toast.error('Failed to generate cycles')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Cycles</DialogTitle>
          <DialogDescription>
            Auto-generate multiple cycles with sequential dates
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="gen-start" className="text-sm">First cycle starts on</Label>
            <Input
              id="gen-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-sm">Duration</Label>
            <p className="text-sm text-muted-foreground mt-0.5">
              {cycleDurationWeeks ? `${cycleDurationWeeks} week${cycleDurationWeeks > 1 ? 's' : ''} per cycle` : 'Set cycle duration in project settings first'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="gen-count" className="text-sm">Number of cycles</Label>
              <Input
                id="gen-count"
                type="number"
                min="1"
                max="12"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="gen-prefix" className="text-sm">Name prefix</Label>
              <Input
                id="gen-prefix"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="Sprint"
                className="mt-1.5"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!startDate || !cycleDurationWeeks || isGenerating}
          >
            {isGenerating ? 'Generating...' : `Generate ${count} Cycles`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
